import { useState } from 'react';
import type { ButtonHTMLAttributes, HTMLAttributes, ReactElement, ReactNode, Ref } from 'react';
import { Icon, type IconName } from '@luacentrix/icons';
import { cn, variant } from './utils';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ControlSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ControlSize;
  loading?: boolean;
  icon?: IconName;
  iconRight?: IconName;
  children?: ReactNode;
}

export function Button({
  variant: tone = 'secondary',
  size = 'md',
  loading = false,
  icon,
  iconRight,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  const isDisabled = disabled === true || loading;
  return (
    <button
      type="button"
      className={cn('lc-button', `lc-button--${tone}`, `lc-button--${size}`, className)}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Icon name="loader" size="sm" className="lc-spinner" /> : null}
      {!loading && icon !== undefined ? <Icon name={icon} size="sm" /> : null}
      {children === undefined ? null : <span className="lc-button__label">{children}</span>}
      {!loading && iconRight !== undefined ? <Icon name={iconRight} size="sm" /> : null}
    </button>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  name: IconName;
  label: string;
  size?: ControlSize;
  tone?: 'secondary' | 'ghost' | 'danger';
  ref?: Ref<HTMLButtonElement>;
}

export function IconButton({
  name,
  label,
  size = 'md',
  tone = 'ghost',
  className,
  ...rest
}: IconButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      className={cn(
        'lc-button',
        `lc-button--${tone}`,
        `lc-button--${size}`,
        'lc-button--icon',
        className,
      )}
      {...rest}
    >
      <Icon name={name} size={size === 'lg' ? 'lg' : 'sm'} />
    </button>
  );
}

export function Spinner({ size = 'md', label }: { size?: ControlSize; label?: string }) {
  return (
    <Icon
      name="loader"
      size={size === 'lg' ? 'lg' : 'sm'}
      className="lc-spinner"
      label={label ?? 'Loading'}
    />
  );
}

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

export function Badge({
  tone = 'neutral',
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLSpanElement> & { tone?: BadgeTone }) {
  return (
    <span className={cn('lc-badge', `lc-badge--${tone}`, className)} {...rest}>
      {children}
    </span>
  );
}

export type IntegrationState =
  | 'connected'
  | 'not_connected'
  | 'pending'
  | 'configuration_required'
  | 'unavailable'
  | 'disabled'
  | 'error'
  | 'expired';

const stateLabels: Record<IntegrationState, string> = {
  connected: 'Connected',
  not_connected: 'Not connected',
  pending: 'Pending',
  configuration_required: 'Configuration required',
  unavailable: 'Unavailable',
  disabled: 'Disabled',
  error: 'Error',
  expired: 'Expired',
};

const stateTone: Record<IntegrationState, 'success' | 'warning' | 'danger' | 'neutral'> = {
  connected: 'success',
  not_connected: 'neutral',
  pending: 'warning',
  configuration_required: 'warning',
  unavailable: 'neutral',
  disabled: 'neutral',
  error: 'danger',
  expired: 'danger',
};

export function StatusIndicator({ state, label }: { state: IntegrationState; label?: string }) {
  return (
    <span className={cn('lc-status', `lc-status--${stateTone[state]}`)}>
      <span className="lc-status__dot" aria-hidden />
      {label ?? stateLabels[state]}
    </span>
  );
}

export function Divider({ className, ...rest }: HTMLAttributes<HTMLHRElement>) {
  return <hr className={cn('lc-divider', className)} {...rest} />;
}

export function Container({
  wide,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { wide?: boolean }) {
  return <div className={cn('lc-container', wide && 'lc-container--wide', className)} {...rest} />;
}

export function Stack({
  gap = 'normal',
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { gap?: 'tight' | 'normal' | 'loose' }) {
  return (
    <div
      className={cn(
        'lc-stack',
        variant('lc-stack', { tight: gap === 'tight', loose: gap === 'loose' }),
        className,
      )}
      {...rest}
    />
  );
}

export function Row({
  align = 'between',
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { align?: 'start' | 'end' | 'between' }) {
  return <div className={cn('lc-row', `lc-row--${align}`, className)} {...rest} />;
}

export function Grid({
  columns,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { columns?: 2 | 3 }) {
  return (
    <div
      className={cn('lc-grid', columns === undefined ? null : `lc-grid--${columns}`, className)}
      {...rest}
    />
  );
}

export function Card({
  flat,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { flat?: boolean }) {
  return <div className={cn('lc-card', flat && 'lc-card--flat', className)} {...rest} />;
}

export function Stat({
  label,
  value,
  delta,
  className,
}: {
  label: string;
  value: string;
  delta?: string;
  className?: string;
}) {
  return (
    <div className={cn('lc-stat', className)}>
      <span className="lc-stat__label">{label}</span>
      <strong className="lc-stat__value">{value}</strong>
      {delta === undefined ? null : <span className="lc-stat__delta">{delta}</span>}
    </div>
  );
}

export function Skeleton({
  height = 12,
  width = '100%',
  className,
}: {
  height?: number | string;
  width?: number | string;
  className?: string;
}) {
  return (
    <span
      className={cn('lc-skeleton', className)}
      style={{
        height: typeof height === 'number' ? `${height}px` : height,
        width: typeof width === 'number' ? `${width}px` : width,
      }}
      aria-hidden
    />
  );
}

export function EmptyState({
  icon = 'stack',
  title,
  description,
  action,
}: {
  icon?: IconName;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="lc-empty">
      <Icon name={icon} size="xl" />
      <h3 className="lc-empty__title">{title}</h3>
      {description === undefined ? null : <p className="lc-empty__description">{description}</p>}
      {action ?? null}
    </div>
  );
}

export type AlertTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

const alertIcons: Record<AlertTone, IconName> = {
  neutral: 'alertCircle',
  success: 'check',
  warning: 'alertTriangle',
  danger: 'alertCircle',
  info: 'alertCircle',
};

export function Alert({
  tone = 'neutral',
  title,
  children,
  action,
}: {
  tone?: AlertTone;
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div
      className={cn('lc-alert', tone === 'neutral' ? null : `lc-alert--${tone}`)}
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
    >
      <Icon name={alertIcons[tone]} size="md" />
      <div className="lc-stack lc-stack--tight" style={{ flex: 1 }}>
        {title === undefined ? null : <p className="lc-alert__title">{title}</p>}
        {children === undefined ? null : <p className="lc-alert__body">{children}</p>}
      </div>
      {action ?? null}
    </div>
  );
}

export function Progress({
  value,
  max = 100,
  label,
}: {
  value: number;
  max?: number;
  label?: string;
}) {
  const ratio = Math.max(0, Math.min(1, value / max));
  return (
    <div
      className="lc-progress"
      role="progressbar"
      aria-valuenow={Math.round(ratio * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div className="lc-progress__bar" style={{ width: `${ratio * 100}%` }} />
    </div>
  );
}

export const AVATAR_SIZES = [24, 32, 40, 64, 96] as const;

export type AvatarSize = (typeof AVATAR_SIZES)[number];

export type AvatarState = 'initials' | 'loading' | 'loaded' | 'error';

const avatarSizeClasses: Record<AvatarSize, string> = {
  24: 'lc-avatar--24',
  32: 'lc-avatar--32',
  40: 'lc-avatar--40',
  64: 'lc-avatar--64',
  96: 'lc-avatar--96',
};

export interface AvatarProps {
  name: string;
  initials?: string | undefined;
  image?: string | undefined;
  size?: AvatarSize;
  className?: string | undefined;
}

export function initialsOf(name: string, initials?: string | undefined): string {
  const provided = initials?.trim() ?? '';
  if (provided.length > 0) {
    return provided.slice(0, 2).toUpperCase();
  }
  const words = name
    .trim()
    .split(/\s+/)
    .filter(word => word.length > 0);
  const first = words[0];
  if (first === undefined) {
    return '';
  }
  const last = words[words.length - 1] ?? first;
  const letters =
    words.length === 1 ? first.slice(0, 2) : `${first.slice(0, 1)}${last.slice(0, 1)}`;
  return letters.toUpperCase();
}

export function Avatar({ name, initials, image, size = 32, className }: AvatarProps): ReactElement {
  const [loadedSource, setLoadedSource] = useState<string | null>(null);
  const [failedSource, setFailedSource] = useState<string | null>(null);
  const source = image === undefined || image.trim().length === 0 ? null : image.trim();
  const state: AvatarState =
    source === null
      ? 'initials'
      : failedSource === source
        ? 'error'
        : loadedSource === source
          ? 'loaded'
          : 'loading';
  const showsImage = source !== null && state !== 'error';

  const captureCachedImage = (node: HTMLImageElement | null): void => {
    if (node !== null && node.complete && node.naturalWidth > 0) {
      setLoadedSource(source);
    }
  };

  return (
    <span
      className={cn('lc-avatar', avatarSizeClasses[size], className)}
      data-avatar-size={String(size)}
      data-avatar-state={state}
      title={name}
    >
      <span
        className="lc-avatar__fallback"
        role={showsImage ? undefined : 'img'}
        aria-hidden={showsImage ? true : undefined}
        aria-label={showsImage ? undefined : name}
      >
        {initialsOf(name, initials)}
      </span>
      {showsImage ? (
        <img
          ref={captureCachedImage}
          className={cn('lc-avatar__image', state === 'loaded' && 'lc-avatar__image--loaded')}
          src={source}
          alt={name}
          width={size}
          height={size}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoadedSource(source)}
          onError={() => setFailedSource(source)}
        />
      ) : null}
    </span>
  );
}

export interface AvatarGroupProps {
  people: Array<{ name: string; image?: string | undefined }>;
  size?: AvatarSize;
  className?: string | undefined;
}

export function AvatarGroup({ people, size = 32, className }: AvatarGroupProps): ReactElement {
  const shown = people.slice(0, 4);
  const hidden = people.length - shown.length;
  return (
    <span className={cn('lc-avatar-group', className)}>
      {shown.map(person => (
        <Avatar key={person.name} name={person.name} image={person.image} size={size} />
      ))}
      {hidden > 0 ? (
        <span
          className={cn('lc-avatar', avatarSizeClasses[size], 'lc-avatar--overflow')}
          role="img"
          aria-label={`${String(hidden)} more`}
        >
          <span className="lc-avatar__fallback" aria-hidden="true">
            {`+${String(hidden)}`}
          </span>
        </span>
      ) : null}
    </span>
  );
}
